import { useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ChevronUp, BarChart3, Settings, RotateCcw, ChevronLeft, ChevronRight, FlipVertical } from 'lucide-react';
import { NativeChessBoard } from './NativeChessBoard';
import { EvaluationBar } from './EvaluationBar';
import { MoveHistory } from './MoveHistory';
import { GameState, Position } from '@/lib/chess-engine';

interface MobileAnalysisBoardProps {
  gameState: GameState;
  gameHistory: GameState[];
  currentMoveIndex: number;
  onMove: (move: { from: Position; to: Position }) => void;
  onGoToMove: (index: number) => void;
  analysisState: {
    analysis: any;
    isAnalyzing: boolean;
    error: string | null;
  };
  orientation: 'white' | 'black';
  onOrientationChange: (orientation: 'white' | 'black') => void;
  analysisEnabled: boolean;
  onAnalysisEnabledChange: (enabled: boolean) => void;
}

export function MobileAnalysisBoard({
  gameState,
  gameHistory,
  currentMoveIndex,
  onMove,
  onGoToMove,
  analysisState,
  orientation,
  onOrientationChange,
  analysisEnabled,
  onAnalysisEnabledChange
}: MobileAnalysisBoardProps) {
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const handleSquareClick = useCallback((position: Position) => {
    if (selectedSquare) {
      if (selectedSquare[0] === position[0] && selectedSquare[1] === position[1]) {
        setSelectedSquare(null);
      } else {
        onMove({ from: selectedSquare, to: position });
        setSelectedSquare(null);
      }
    } else {
      const piece = gameState.board[position[0]][position[1]];
      if (piece && piece.color === gameState.sideToMove) {
        setSelectedSquare(position);
      }
    }
  }, [selectedSquare, gameState, onMove]);

  const evaluation = analysisState.analysis?.evaluation ?? 0;
  const bestMove = analysisState.analysis?.bestMove;

  return (
    <div className="mobile-chess-container safe-area-top safe-area-bottom bg-background">
      {/* Top Status Bar */}
      <div className="flex items-center justify-between p-3 bg-card border-b safe-area-left safe-area-right">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onGoToMove(0)}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant={analysisState.isAnalyzing ? "secondary" : "outline"}>
            {analysisState.isAnalyzing ? 'Analyzing' : 'Ready'}
          </Badge>
          <span className="text-sm text-muted-foreground">
            Move {currentMoveIndex} of {gameHistory.length - 1}
          </span>
        </div>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => setShowSettings(true)}
        >
          <Settings className="w-4 h-4" />
        </Button>
      </div>

      {/* Chess Board - Main Focus */}
      <div className="mobile-board-container">
        <div className="mobile-chess-board">
          <NativeChessBoard
            gameState={gameState}
            onSquareClick={handleSquareClick}
            selectedSquare={selectedSquare}
            flipped={orientation === 'black'}
            enableSwipeGestures={true}
            fillParent={true}
            compactPadding={true}
          />
        </div>
      </div>

      {/* Evaluation Bar */}
      <div className="px-4 py-2">
        <EvaluationBar 
          evaluation={evaluation}
          showNumbers={true}
          showPlayerLabels={false}
          className="h-3"
        />
      </div>

      {/* Move Controls */}
      <div className="flex items-center justify-center gap-4 p-3 bg-card border-t">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onGoToMove(Math.max(0, currentMoveIndex - 1))}
          disabled={currentMoveIndex === 0}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onGoToMove(0)}
          disabled={currentMoveIndex === 0}
        >
          <RotateCcw className="w-4 h-4" />
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onGoToMove(Math.min(gameHistory.length - 1, currentMoveIndex + 1))}
          disabled={currentMoveIndex === gameHistory.length - 1}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Analysis Panel - Bottom Sheet */}
      <Sheet open={showAnalysis} onOpenChange={setShowAnalysis}>
        <SheetTrigger asChild>
          <Button 
            variant="outline" 
            className="mx-4 mb-3"
            onClick={() => setShowAnalysis(true)}
          >
            <ChevronUp className="w-4 h-4 mr-2" />
            Analysis & Moves
            <BarChart3 className="w-4 h-4 ml-2" />
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[60vh] p-4">
          <div className="space-y-4">
            {/* Engine Analysis */}
            {analysisState.analysis && (
              <Card className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Engine Analysis</span>
                  <Badge variant="outline">
                    Eval: {typeof evaluation === 'number' ? evaluation.toFixed(2) : '0.00'}
                  </Badge>
                </div>
                {bestMove && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Best: </span>
                    <span className="font-mono font-medium">{bestMove}</span>
                  </div>
                )}
              </Card>
            )}

            {/* Move History */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Move History</h3>
              <MoveHistory 
                gameStates={gameHistory}
                onMoveClick={onGoToMove}
                currentMoveIndex={currentMoveIndex}
              />
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Settings Sheet */}
      <Sheet open={showSettings} onOpenChange={setShowSettings}>
        <SheetContent side="bottom" className="h-[50vh] p-4">
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Board Settings</h3>
            
            {/* Board Orientation */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Board Orientation</Label>
                <p className="text-sm text-muted-foreground">Flip the board</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOrientationChange(orientation === 'white' ? 'black' : 'white')}
              >
                <FlipVertical className="w-4 h-4 mr-2" />
                {orientation === 'white' ? 'White' : 'Black'}
              </Button>
            </div>

            {/* Live Analysis Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="mobile-analysis-toggle" className="text-base">
                  Live Analysis
                </Label>
                <p className="text-sm text-muted-foreground">
                  Real-time engine evaluation
                </p>
              </div>
              <Switch
                id="mobile-analysis-toggle"
                checked={analysisEnabled}
                onCheckedChange={onAnalysisEnabledChange}
              />
            </div>

            {/* Info */}
            <Card className="p-3 bg-muted">
              <p className="text-sm text-muted-foreground">
                Tap pieces to select, then tap destination to move. You can also swipe from piece to destination.
              </p>
            </Card>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}